/**
 * 🌙 Google Opal Dark Theme
 * Tema oscuro elegante con paleta sofisticada y excelente contraste
 * 
 * @description Versión dark del tema Google Opal con colores ricos,
 * perfecta legibilidad y ambiente profesional para trabajo nocturno
 */

export const googleOpalDarkTheme = {
  name: 'Google Opal Dark',
  mode: 'dark',
  
  // 🎨 Paleta de colores oscura sofisticada
  palette: {
    primary: {
      main: '#60A5FA',        // Azul más claro para modo oscuro
      light: '#93C5FD',       // Azul muy claro
      dark: '#3B82F6',        // Azul medio
      contrastText: '#1F2937'
    },
    secondary: {
      main: '#C084FC',        // Púrpura suave para oscuro
      light: '#DDD6FE',       // Púrpura muy claro
      dark: '#A855F7',        // Púrpura medio
      contrastText: '#1F2937'
    },
    error: {
      main: '#F87171',
      light: '#FCA5A5',
      dark: '#DC2626'
    },
    warning: {
      main: '#FBBF24',
      light: '#FCD34D',
      dark: '#D97706'
    },
    info: {
      main: '#38BDF8',
      light: '#7DD3FC',
      dark: '#0EA5E9'
    },
    success: {
      main: '#4ADE80',
      light: '#86EFAC',
      dark: '#16A34A'
    },
    
    // 🖼️ Fondos oscuros elegantes
    background: {
      default: '#0F172A',      // Fondo principal azul muy oscuro
      paper: '#1E293B',        // Tarjetas gris azulado
      elevated: '#334155'      // Elementos elevados
    },
    
    // 📝 Textos optimizados para oscuro
    text: {
      primary: '#F8FAFC',      // Blanco suave para títulos
      secondary: '#CBD5E1',    // Gris claro para subtextos
      disabled: '#64748B',     // Gris medio para deshabilitados
      hint: '#475569'         // Gris oscuro para hints
    },
    
    // 🎯 Colores de acción
    action: {
      active: '#60A5FA',
      hover: 'rgba(96, 165, 250, 0.08)',
      selected: 'rgba(96, 165, 250, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)'
    },
    
    // 📏 Divisores
    divider: 'rgba(255, 255, 255, 0.12)',

    // 🦅 Colores de marca AI Flock
    brand: {
      main: '#A855F7',          // AI Flock purple (brighter for dark mode)
      light: '#C084FC',         // Purple claro
      dark: '#9333EA',          // Purple oscuro
      contrastText: '#FFFFFF'
    },

    // 🎨 Escala de grises (invertida para dark mode)
    grey: {
      50: '#1E293B',            // Oscuro (suggestion cards bg - matches background.paper)
      100: '#334155',           // Oscuro claro (hover state - matches background.elevated)
      200: '#475569',
      300: '#64748B',
      400: '#94A3B8',
      500: '#CBD5E1',
      600: '#E2E8F0',
      700: '#F1F5F9',
      800: '#F8FAFC',
      900: '#FFFFFF'
    }
  },

  // ✨ Efectos visuales (shadows, overlays, hover states) - Dark Mode
  effects: {
    // Sombras con diferentes intensidades (más pronunciadas en dark mode)
    shadows: {
      subtle: '0px 1px 4px rgba(0, 0, 0, 0.3)',
      medium: '0px 2px 8px rgba(0, 0, 0, 0.4)',
      strong: '0px 4px 20px rgba(0, 0, 0, 0.6)',
      brand: '0 4px 20px rgba(96, 165, 250, 0.3)',      // Sombra con color primario (azul claro)
      brandMedium: '0 2px 8px rgba(96, 165, 250, 0.15)',
      brandStrong: '0 4px 16px rgba(96, 165, 250, 0.25)'
    },

    // Estados hover (overlay claro sobre fondo oscuro)
    hover: {
      background: 'rgba(255, 255, 255, 0.05)',          // Hover universal dark mode
      primary: 'rgba(96, 165, 250, 0.1)',               // Hover con color primario
      card: '0px 1px 4px rgba(0, 0, 0, 0.4)',           // Sombra en hover de cards
      cardBackground: 'rgba(96, 165, 250, 0.08)'        // Background hover de cards
    },

    // Overlays (similar en ambos modos)
    overlay: {
      loading: 'rgba(0, 0, 0, 0.8)',                    // Overlay oscuro para loading (más oscuro)
      modal: 'rgba(0, 0, 0, 0.7)',                      // Overlay para modales
      subtle: 'rgba(0, 0, 0, 0.5)'                      // Overlay sutil
    },

    // Estados de advertencia (warning) - Adaptado para dark mode
    warning: {
      background: 'rgba(255, 152, 0, 0.2)',             // Background warning (naranja más claro)
      hover: 'rgba(255, 152, 0, 0.3)',                  // Hover warning
      border: 'rgba(255, 152, 0, 0.4)'                  // Border warning
    }
  },

  // 🎛️ Configuración específica por sección
  sections: {
    // 💬 Chat Interface
    chat: {
      background: '#0F172A',
      messageBg: '#1E293B',
      inputBg: '#334155',
      userMessageBg: '#1E40AF',
      botMessageBg: '#1E293B',
      border: '#475569',
      shadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
    },

    // 🎨 Flow Editor - VERSIÓN NOCTURNA ESPECTACULAR
    editor: {
      canvasBg: '#0F172A',          // ⭐ Fondo oscuro profundo
      nodeBg: '#1E293B',            // Nodos gris azulado
      nodeBorder: '#475569',        // Bordes visibles
      nodeSelected: '#60A5FA',      // Azul brillante para selección
      nodeHover: '#334155',         // Hover sutil
      toolbarBg: 'rgba(30, 41, 59, 0.95)', // Toolbar glassmorphism oscuro
      toolbarShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      connectionDefault: '#64748B',  // Conexiones grises
      connectionActive: '#60A5FA',   // Conexiones activas azules
      miniMapBg: 'rgba(51, 65, 85, 0.9)',
      gridColor: '#334155'          // Grid sutil pero visible
    },

    // 👥 Roles Editor
    roles: {
      cardBg: '#1E293B',
      sidebarBg: '#0F172A',
      accent: '#60A5FA',
      border: '#475569',
      hover: '#334155',
      selected: '#1E40AF'
    },

    // ⚙️ Settings
    settings: {
      panelBg: '#0F172A',
      cardBg: '#1E293B',
      border: '#475569',
      accent: '#60A5FA'
    },

    // 🎯 Onboarding
    onboarding: {
      bg: '#0F172A',
      cardBg: '#1E293B',
      accent: '#60A5FA',
      progress: '#93C5FD'
    }
  },

  // 🎭 Tipografía optimizada para oscuro
  typography: {
    fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    
    h1: { fontSize: '2.5rem', fontWeight: 600, color: '#F8FAFC', lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, color: '#F8FAFC', lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 500, color: '#F8FAFC', lineHeight: 1.4 },
    h4: { fontSize: '1.5rem', fontWeight: 500, color: '#E2E8F0', lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 500, color: '#E2E8F0', lineHeight: 1.5 },
    h6: { fontSize: '1.125rem', fontWeight: 500, color: '#CBD5E1', lineHeight: 1.5 },
    
    body1: { fontSize: '1rem', color: '#E2E8F0', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', color: '#CBD5E1', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', color: '#94A3B8', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' }
  },

  // ✨ Sombras dramáticas para modo oscuro
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px rgba(0, 0, 0, 0.4)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.5)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.6)',
    
    // Sombras específicas para componentes
    card: '0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)',
    modal: '0 25px 50px rgba(0, 0, 0, 0.7)',
    tooltip: '0 4px 6px rgba(0, 0, 0, 0.5)'
  },

  // 📏 Espaciado consistente (igual que light)
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    xxl: '3rem'       // 48px
  },

  // 🎭 Animaciones suaves (igual que light)
  animations: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    
    // Animaciones específicas
    fadeIn: 'fadeIn 200ms ease-out',
    slideUp: 'slideUp 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'bounce 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },

  // 📱 Breakpoints responsivos (igual que light)
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  },

  // 🎨 Overrides de componentes MUI para modo oscuro
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0F172A !important',
          color: '#E2E8F0',
          fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        // ✅ ARREGLO: Asegurar que todo el HTML tenga fondo oscuro
        html: {
          backgroundColor: '#0F172A !important'
        },
        // ✅ ARREGLO: Root container también oscuro
        '#root': {
          backgroundColor: '#0F172A !important',
          minHeight: '100dvh'
        },
        // ✅ ARREGLO: Cualquier div principal
        'div[data-testid="main-container"], .main-container, .MuiBox-root': {
          backgroundColor: 'inherit'
        }
      }
    },
    
    MuiPaper: {
      styleOverrides: {
        root: {
          // Don't hardcode backgroundColor - let components use sx prop or default to theme.palette.background.paper
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.4)',
          borderRadius: 8,
          border: '1px solid #475569'
        }
      }
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
          },
          // ✅ ARREGLO: Mejor contraste para botones deshabilitados en modo oscuro
          '&.Mui-disabled': {
            backgroundColor: 'rgba(71, 85, 105, 0.3)', // Gris azulado visible
            color: 'rgba(203, 213, 225, 0.6)', // Texto gris claro con mejor contraste
            border: '1px solid rgba(71, 85, 105, 0.4)'
          }
        },
        contained: {
          // ✅ ARREGLO: Botones contained deshabilitados
          '&.Mui-disabled': {
            backgroundColor: 'rgba(51, 65, 85, 0.5)', // Fondo más visible
            color: 'rgba(148, 163, 184, 0.8)', // Texto más legible
            backgroundImage: 'none'
          }
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background: 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
          }
        }
      }
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #475569',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.4)',
          backgroundColor: '#1E293B'
        }
      }
    }
  }
};
